/**
 * Deciding a decoration, and reading the citation out.
 *
 * `awardsEarned` compares the pilot's confirmed victories against every medal
 * of his nation, including the repeat "bar" tiers, and returns whatever he has
 * become eligible for that he does not already hold. The thresholds themselves
 * (and the wartime escalation of the Blue Max) live in `medals.ts`.
 *
 * The citation is **not** assembled. `MEDAL.DAT` holds one pre-written template
 * per decoration and the game fills five slots in it; `medal-citations.ts` is
 * that corpus and its expander, and `citationLines` below is a two-line lookup
 * on top of it. Until 2026-08-26 this module composed a sentence of its own —
 * *"Captain A. Cooper is awarded The Victoria Cross, by Imperial Decree, King
 * George V."* — which put a rank the file never mentions in front of the name
 * and the decree line on all nine medals instead of the three that carry it.
 */

import { formatCareerDate, compareDates, type CareerDate } from "./calendar.ts";
import { citationForMedal, expandCitation } from "./medal-citations.ts";
import { medalById, medalDisplayName, medalThreshold, medalsFor, type Medal } from "./medals.ts";
import type { Nation } from "../mission/mission-roster.ts";
import type { MedalAward } from "./career-state.ts";

/** How many awards of this medal a pilot with this many victories has earned. */
export function earnedLevel(entry: Medal, victories: number, date: CareerDate): number {
  if (entry.notBefore && compareDates(date, entry.notBefore) < 0) return 0;
  const first = medalThreshold(entry, date);
  if (victories < first) return 0;
  if (entry.bars === 0 || entry.barVictories === 0) return 1;
  const extra = Math.floor((victories - first) / entry.barVictories);
  return 1 + Math.min(extra, entry.bars);
}

function heldLevel(held: readonly MedalAward[], medalId: string): number {
  let level = 0;
  for (const award of held) {
    if (award.medalId === medalId) level = Math.max(level, award.bars + 1);
  }
  return level;
}

export interface AwardContext {
  readonly nation: Nation;
  readonly victories: number;
  readonly date: CareerDate;
  readonly held: readonly MedalAward[];
}

/** Everything this pilot has just become entitled to, in medal-table order. */
export function awardsEarned(context: AwardContext): readonly MedalAward[] {
  const earned: MedalAward[] = [];
  for (const entry of medalsFor(context.nation)) {
    const level = earnedLevel(entry, context.victories, context.date);
    if (level <= heldLevel(context.held, entry.id)) continue;
    earned.push({ medalId: entry.id, bars: level - 1, date: context.date, victories: context.victories });
  }
  return earned;
}

export function awardScore(award: MedalAward): number {
  return medalById(award.medalId)?.score ?? 0;
}

/** `"The Military Cross (M.C.) with Bar"`. */
export function awardName(award: MedalAward): string {
  const entry = medalById(award.medalId);
  return entry ? medalDisplayName(entry, award.bars) : award.medalId;
}

export interface CitationSubject {
  /** `%n`. The bare name: no rank, because the file's other `%n`s carry none. */
  readonly name: string;
  readonly nation: Nation;
}

/**
 * The citation screen's body, line by line — `MEDAL.DAT`'s template with its
 * slots filled, and nothing else. `CONGRATULATIONS!!` is the screen's *title*
 * (`AWARD_HEADER`), so it is not in here.
 *
 * The fallback matters: `medals.ts` can in principle carry a decoration
 * `MEDAL.DAT` has no template for, and a decorated pilot being handed a blank
 * screen would be worse than a plain sentence. It is unreachable for all nine of
 * today's medals, and `campaign-medals.test.ts` asserts that it is.
 */
export function citationLines(award: MedalAward, subject: CitationSubject): readonly string[] {
  const citation = citationForMedal(award.medalId);
  if (!citation) {
    return [`${subject.name} is awarded ${awardName(award)}.`, `Dated ${formatCareerDate(award.date)}, for ${award.victories} confirmed victories.`];
  }
  return expandCitation(citation, {
    pilot: subject.name,
    victories: award.victories,
    nation: subject.nation,
    bars: award.bars,
  });
}
